import { MenuRecipeOverrideUpdate, RecipeItem } from '..';
import { IngredientWithAmount } from '../types';
import { OverrideUpdate } from '@/features';

import { formatDecimal } from './priceCalculator';

export const formatDecimalValueNumberInput = (value: string, decimalPlaces: number = 6) =>
  value
    .replace(/,/g, '.') // Allow comma as decimal
    .replace(/^0+(\d)/, '$1') // Remove leading zeros before numbers
    .replace(/^\./, '0.') // Add leading zero before decimal
    .replace(/\.{2,}/g, '.') // Replace multiple decimals with single
    .replace(/(\..*)\./g, '$1') // Allow only one decimal point
    .replace(/[^0-9.]/g, '') // Remove non-numeric characters
    .replace(new RegExp(`(\\d*\\.\\d{0,${decimalPlaces}})\\d*`), '$1'); // Allow only {decimalPlaces} number of decimal places

export const formatIntValueNumberInput = (value: string) =>
  value
    .replace(/^0+(\d)/, '$1') // Remove leading zeros before numbers
    .replace(/[^0-9]/g, ''); // Remove all non-numeric characters

export const formatPriceDifferenceValueInNumberInput = (value: string) => {
  // Special case for when user is just typing a sign
  if (value === '-' || value === '+') {
    return value;
  }

  // First handle the sign at the beginning
  let sign = '';
  let valueWithoutSign = value;

  if (value.startsWith('+') || value.startsWith('-')) {
    sign = value.charAt(0);
    valueWithoutSign = value.substring(1);
  }

  // Clean the rest of the value without the sign
  const cleanedValue = valueWithoutSign
    .replace(/,/g, '.') // Allow comma as decimal
    .replace(/^0+(\d)/, '$1') // Remove leading zeros before numbers
    .replace(/^\./, '0.') // Add leading zero before decimal
    .replace(/\.{2,}/g, '.') // Replace multiple decimals with single
    .replace(/(\..*)\./g, '$1') // Allow only one decimal point
    .replace(/[^0-9.]/g, ''); // Remove all non-numeric characters

  // Always preserve the sign, even if the rest is empty
  if (sign) {
    return cleanedValue ? sign + cleanedValue : sign;
  }

  // Add plus sign if there's a value but no sign
  if (cleanedValue && !sign) {
    return `+${cleanedValue}`;
  }

  return cleanedValue;
};

export const convertFormRecordToActionIngredientArray = (
  ingredientsToBeModified: Record<string, { amount: string }>,
  productIngredientsWithAmount: IngredientWithAmount[],
): OverrideUpdate[] => {
  const result: OverrideUpdate[] = [];

  for (const [id, value] of Object.entries(ingredientsToBeModified || {})) {
    // Skip invalid values
    if (!value.amount || parseFloat(value.amount) <= 0) continue;

    const ingredientId = parseInt(id);
    const originalIngredient = productIngredientsWithAmount.find(
      (item) => item.ingredient.id === ingredientId,
    );

    // Skip if original ingredient not found
    if (!originalIngredient) continue;

    const initialAmount = parseFloat(formatDecimal(originalIngredient.amount));
    const newAmount = Number(value.amount);
    const amountDifference = newAmount - initialAmount;

    // Skip if no change in amount
    if (amountDifference === 0) continue;

    result.push({
      amount_change: amountDifference,
      ingredient_id: ingredientId,
    });
  }
  return result;
};

export const convertFormRecordToMenuRecipeOverrideUpdateArray = (
  ingredientsToBeModified: Record<string, { amount: string }>,
  recipeItems: RecipeItem[],
): MenuRecipeOverrideUpdate[] => {
  const result: MenuRecipeOverrideUpdate[] = [];
  for (const [id, value] of Object.entries(ingredientsToBeModified || {})) {
    if (!value.amount || parseFloat(value.amount) <= 0) continue;

    const productId = parseInt(id);

    const originalIngredient = recipeItems.find((item) => item.product.id === productId);
    if (!originalIngredient) continue;

    const initialAmount = parseFloat(formatDecimal(originalIngredient.amount));
    const newAmount = Number(value.amount);
    const amountDifference = newAmount - initialAmount;
    if (amountDifference === 0) continue;

    result.push({
      product_id: productId,
      amount_change: amountDifference,
    });
  }
  return result;
};
